import { supabase } from './supabaseClient';
async function call(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw new Error(error.code === 'PGRST202' ? 'Employee email login setup is required. Ask HR to run supabase/employee_email_auth_update.sql.' : error.message || 'Employee login request failed.');
  if (data?.error) throw new Error(data.error);
  return data;
}
export function attachEmployeeSession(employee, token) {
  const result = { ...employee };
  Object.defineProperty(result, 'session_token', { value: token, enumerable: false });
  return result;
}
export function employeeToken(employee) {
  if (!employee?.id || !employee.session_token) throw new Error('Please sign in again. Your employee session is unavailable.');
  return String(employee.session_token);
}
export async function employeeAuthSession() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user) return null;
  const session = await call('employee_auth_session');
  if (session?.role !== 'employee' || session.userId !== data.user.id || !session.employeeId || typeof session.passwordChangeRequired !== 'boolean' || (!session.passwordChangeRequired && !session.employeeSessionToken)) throw new Error('Could not confirm employee access. Ask HR for an invitation.');
  return session;
}
export async function employeeLogin(email, password) {
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
  if (error) throw new Error('Unable to sign in. Check your email and password.');
  try {
    const session = await employeeAuthSession();
    if (!session) throw new Error('Please sign in again.');
    return session;
  } catch (error) { await supabase.auth.signOut(); throw error; }
}
export async function resumeEmployeeSession(session) {
  if (!session?.employeeId || !session.employeeSessionToken) throw new Error('Please sign in again to update your employee session.');
  const result = await call('employee_portal_session', { p_employee_id: session.employeeId, p_token: session.employeeSessionToken });
  if (result?.employee?.id !== session.employeeId || result.forcePinChange !== false) throw new Error('Could not confirm your employee session. Sign in again.');
  return result;
}
export function validatePassword(password) {
  if (Array.from(password).length < 12) throw new Error('Use at least 12 characters for your password.');
  if (new Blob([password]).size > 72) throw new Error('Your password is too long. Use up to 72 bytes.');
}
export async function setEmployeeTemporaryPassword(companyId, employeeId, password) {
  validatePassword(password);
  const { data } = await supabase.auth.getSession();
  if (!data?.session?.access_token) throw new Error('Please sign in again.');
  const response = await fetch('/api/employee-password', {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` },
    body: JSON.stringify({ company_id: companyId, employee_id: employeeId, password })
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || result?.ok !== true || result.passwordChangeRequired !== true) throw new Error(result?.error || 'Could not set the temporary password.');
  return result;
}
export async function requestPasswordReset(email, employee = true) {
  const address = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) throw new Error('Enter your registered email address first.');
  const redirect = new URL('/', window.location.origin);
  redirect.searchParams.set('password_setup', '1');
  if (employee) redirect.searchParams.set('mode', 'employee');
  const { error } = await supabase.auth.resetPasswordForEmail(address, { redirectTo: redirect.href });
  if (error && error.status === 429) throw new Error('Please wait before requesting another reset link.');
  if (error) throw new Error('A reset email could not be sent. Wait and try again or contact your administrator.');
  return 'If an account exists for this email, a password reset link has been sent. Check your inbox and spam folder.';
}
export async function employeeLogout(session) {
  try {
    if (session?.employeeId && session.employeeSessionToken) await call('employee_logout', { p_employee_id: session.employeeId, p_token: session.employeeSessionToken });
  } finally { await supabase.auth.signOut(); }
}
